import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Brain, Zap, TrendingUp, AlertCircle, CheckCircle, Layers, Code, FileText, Globe, Terminal, ArrowRight } from 'lucide-react';

const BSDCLIProject = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [activeTab, setActiveTab] = useState('evolution');

  const evolutionStages = [
    {
      id: 'bsd-agent',
      name: 'BSD Agent',
      icon: AlertCircle,
      color: 'from-red-500 to-orange-500',
      bgColor: 'from-red-500/20 to-orange-500/20',
      borderColor: 'border-red-500/30',
      status: 'legacy',
      title: 'Initial Approach - Server-Side Multi-Agent',
      description: 'First-generation architecture with multiple agents',
      problems: [
        'Multi-agent architecture on server',
        'Manual knowledge base updates',
        'Unstable RAG retrieval in large docs',
        'Weak generalization capability'
      ],
      image: '/pic/bsd_cli/bsd_agent_architecture.webp'
    },
    {
      id: 'bsd-cli-v1',
      name: 'BSD CLI v1',
      icon: Zap,
      color: 'from-blue-500 to-cyan-500',
      bgColor: 'from-blue-500/20 to-cyan-500/20',
      borderColor: 'border-blue-500/30',
      status: 'breakthrough',
      title: 'Agentic CLI - Local & Intelligent',
      description: 'Claude Code-inspired with MCP integration',
      features: [
        'Local CLI with agentic capabilities',
        'Kimi Kosong framework',
        'Desktop Commander MCP (file ops)',
        'Playwright MCP (browser automation)',
        'Real-time project analysis',
        'Six-dimensional analysis framework',
        '76% token optimization'
      ],
      image: '/pic/bsd_cli/bsd_cli_architecture.webp'
    },
    {
      id: 'bsd-cli-v2',
      name: 'BSD CLI v2',
      icon: CheckCircle,
      color: 'from-green-500 to-emerald-500',
      bgColor: 'from-green-500/20 to-emerald-500/20',
      borderColor: 'border-green-500/30',
      status: 'enhanced',
      title: 'Enhanced Experience - Production Ready',
      description: 'Optimized session control & user experience',
      improvements: [
        'Advanced session management',
        'Smart context compression (10:1 ratio)',
        'Multi-session parallel support',
        'Checkpoint & rollback mechanism',
        'Session recovery',
        'More user-friendly interface'
      ],
      image: '/pic/bsd_cli/bsd_cli_layout.webp'
    }
  ];

  const sixDimensions = [
    {
      name: 'Target Analysis',
      icon: Layers,
      color: 'from-purple-500 to-pink-500',
      description: 'Extract core objectives, success metrics, OKRs'
    },
    {
      name: 'Summary Analysis',
      icon: FileText,
      color: 'from-blue-500 to-cyan-500',
      description: 'Generate concise overview with key features'
    },
    {
      name: 'Details Analysis',
      icon: Code,
      color: 'from-green-500 to-teal-500',
      description: 'Break down features, user stories, acceptance criteria'
    },
    {
      name: 'Layout Analysis',
      icon: Globe,
      color: 'from-orange-500 to-red-500',
      description: 'Analyze UI structure, interaction flows, navigation'
    },
    {
      name: 'Code Analysis',
      icon: Terminal,
      color: 'from-indigo-500 to-purple-500',
      description: 'Examine APIs, business logic, data models'
    },
    {
      name: 'Gap Analysis',
      icon: TrendingUp,
      color: 'from-yellow-500 to-amber-500',
      description: 'Compare requirements with current state'
    }
  ];

  const mcpIntegrations = [
    {
      name: 'Desktop Commander',
      icon: Code,
      color: 'text-blue-400',
      description: 'File system operations - Read/write local files',
      capabilities: ['dc_read_file', 'dc_write_file', 'dc_list_directory']
    },
    {
      name: 'Playwright',
      icon: Globe,
      color: 'text-green-400',
      description: 'Browser automation - Get real-time project info',
      capabilities: ['pw_navigate', 'pw_screenshot', 'pw_get_content']
    }
  ];

  const renderEvolutionContent = () => (
    <div className="space-y-6">
      {evolutionStages.map((stage, index) => {
        const Icon = stage.icon;
        return (
          <motion.div
            key={stage.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className={`bg-gradient-to-br ${stage.bgColor} rounded-xl border ${stage.borderColor} overflow-hidden`}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
              {/* Left: Image */}
              <div className="relative bg-gray-900/50 p-6 flex items-center justify-center min-h-[350px]">
                <motion.img
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: index * 0.1 + 0.2 }}
                  src={stage.image}
                  alt={stage.name}
                  className="max-w-full max-h-[320px] object-contain rounded-lg border-2 border-white/10 cursor-pointer hover:border-white/30 transition-all"
                  onClick={() => setSelectedImage({ src: stage.image, title: stage.name })}
                />
                {index < evolutionStages.length - 1 && (
                  <div className="absolute -right-3 top-1/2 transform -translate-y-1/2 z-10">
                    <ArrowRight className="w-6 h-6 text-gray-400" />
                  </div>
                )}
              </div>

              {/* Right: Content */}
              <div className="p-6">
                <div className="flex items-center gap-3 mb-3">
                  <div className={`bg-gradient-to-br ${stage.color} p-2 rounded-lg`}>
                    <Icon className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-bold text-lg text-white">{stage.name}</h4>
                    <p className="text-xs text-gray-400">{stage.title}</p>
                  </div>
                </div>

                <p className="text-sm text-gray-300 mb-4">{stage.description}</p>

                {stage.problems ? (
                  <div className="space-y-2">
                    <p className="text-xs text-red-400 font-semibold">Challenges:</p>
                    <ul className="space-y-1">
                      {stage.problems.map((problem, idx) => (
                        <li key={idx} className="text-xs text-gray-300 flex items-start gap-2">
                          <span className="text-red-400 mt-0.5">✗</span>
                          <span>{problem}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <p className="text-xs text-green-400 font-semibold">
                      {stage.features ? 'Key Features:' : 'Improvements:'}
                    </p>
                    <ul className="space-y-1">
                      {(stage.features || stage.improvements).map((item, idx) => (
                        <li key={idx} className="text-xs text-gray-300 flex items-start gap-2">
                          <span className="text-green-400 mt-0.5">✓</span>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        );
      })}
    </div>
  );

  const renderTechnicalContent = () => (
    <div className="space-y-6">
      {/* MCP Integrations */}
      <div className="bg-white/5 rounded-xl p-6 border border-blue-500/30">
        <h4 className="text-lg font-bold text-blue-400 mb-4 flex items-center gap-2">
          <Layers className="w-5 h-5" />
          MCP Integrations - Agentic Capabilities
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {mcpIntegrations.map((mcp, index) => {
            const Icon = mcp.icon;
            return (
              <motion.div
                key={mcp.name}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-gradient-to-br from-blue-500/10 to-cyan-500/10 rounded-lg p-4 border border-blue-500/20"
              >
                <div className="flex items-center gap-2 mb-3">
                  <Icon className={`w-5 h-5 ${mcp.color}`} />
                  <h5 className="font-bold text-white">{mcp.name}</h5>
                </div>
                <p className="text-xs text-gray-300 mb-3">{mcp.description}</p>
                <div className="space-y-1">
                  <p className="text-xs text-gray-400">Capabilities:</p>
                  {mcp.capabilities.map((cap, idx) => (
                    <div key={idx} className="bg-black/30 px-2 py-1 rounded text-xs text-cyan-400 font-mono">
                      {cap}
                    </div>
                  ))}
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* Six Dimensions Framework */}
      <div className="bg-white/5 rounded-xl p-6 border border-purple-500/30">
        <h4 className="text-lg font-bold text-purple-400 mb-4 flex items-center gap-2">
          <Brain className="w-5 h-5" />
          Six-Dimensional Analysis Framework
        </h4>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {sixDimensions.map((dim, index) => {
            const Icon = dim.icon;
            return (
              <motion.div
                key={dim.name}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: index * 0.1 }}
                className={`bg-gradient-to-br ${dim.color} rounded-lg p-3 relative overflow-hidden`}
              >
                <div className="absolute inset-0 bg-black/20"></div>
                <div className="relative">
                  <Icon className="w-5 h-5 text-white mb-2" />
                  <h5 className="text-sm font-bold text-white mb-1">{dim.name}</h5>
                  <p className="text-xs text-white/80 line-clamp-2">{dim.description}</p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* v2 Enhancements */}
      <div className="bg-white/5 rounded-xl p-6 border border-green-500/30">
        <h4 className="text-lg font-bold text-green-400 mb-4 flex items-center gap-2">
          <CheckCircle className="w-5 h-5" />
          v2 Key Enhancements
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <div className="bg-green-500/20 p-2 rounded-lg">
                <Terminal className="w-4 h-4 text-green-400" />
              </div>
              <div>
                <h5 className="text-sm font-bold text-white">Session Management</h5>
                <p className="text-xs text-gray-400">Auto-save, multi-session, checkpoints, rollback</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="bg-blue-500/20 p-2 rounded-lg">
                <Zap className="w-4 h-4 text-blue-400" />
              </div>
              <div>
                <h5 className="text-sm font-bold text-white">Context Optimization</h5>
                <p className="text-xs text-gray-400">Smart compression (10:1), token monitoring</p>
              </div>
            </div>
          </div>
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <div className="bg-purple-500/20 p-2 rounded-lg">
                <Layers className="w-4 h-4 text-purple-400" />
              </div>
              <div>
                <h5 className="text-sm font-bold text-white">Enhanced UX</h5>
                <p className="text-xs text-gray-400">Rich terminal UI, real-time feedback, better errors</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="bg-amber-500/20 p-2 rounded-lg">
                <TrendingUp className="w-4 h-4 text-amber-400" />
              </div>
              <div>
                <h5 className="text-sm font-bold text-white">Performance</h5>
                <p className="text-xs text-gray-400">84% cost savings, 10x efficiency gain</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="bg-gradient-to-br from-blue-500/10 to-cyan-500/10 rounded-xl border border-blue-500/30 p-6">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="bg-gradient-to-r from-blue-500 to-cyan-500 p-3 rounded-lg">
          <Brain className="w-8 h-8 text-white" />
        </div>
        <div>
          <h3 className="text-2xl font-bold text-blue-400">BSD CLI</h3>
          <p className="text-sm text-gray-400">Intelligent Business Requirement Document Analysis</p>
        </div>
      </div>

      {/* Introduction */}
      <div className="mb-6 p-4 bg-blue-500/10 rounded-lg border border-blue-500/20">
        <p className="text-gray-300 leading-relaxed">
          Evolution from <span className="text-red-400 font-bold">server-side multi-agent</span> to
          <span className="text-blue-400 font-bold"> local agentic CLI</span>, inspired by Claude Code with
          <span className="text-cyan-400 font-bold"> real-time project analysis</span> capabilities through MCP integration.
        </p>
      </div>

      {/* Tab Navigation */}
      <div className="mb-6">
        <div className="flex gap-2 bg-gray-900/50 rounded-lg p-1">
          <button
            onClick={() => setActiveTab('evolution')}
            className={`flex-1 px-4 py-2 rounded-md text-sm font-semibold transition-all ${
              activeTab === 'evolution'
                ? 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white'
                : 'text-gray-400 hover:bg-white/5'
            }`}
          >
            <Layers className="w-4 h-4 inline mr-2" />
            Evolution
          </button>
          <button
            onClick={() => setActiveTab('technical')}
            className={`flex-1 px-4 py-2 rounded-md text-sm font-semibold transition-all ${
              activeTab === 'technical'
                ? 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white'
                : 'text-gray-400 hover:bg-white/5'
            }`}
          >
            <Code className="w-4 h-4 inline mr-2" />
            Technical Details
          </button>
        </div>
      </div>

      {/* Tab Content */}
      <div className="min-h-[500px]">
        {activeTab === 'evolution' ? renderEvolutionContent() : renderTechnicalContent()}
      </div>

      {/* Image Modal */}
      {selectedImage && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50 flex items-center justify-center p-8"
          onClick={() => setSelectedImage(null)}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="relative max-w-6xl max-h-[90vh]"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="absolute -top-12 right-0 text-white hover:text-blue-400 transition-colors"
              onClick={() => setSelectedImage(null)}
            >
              <span className="text-2xl">✕</span>
            </button>
            <img
              src={selectedImage.src}
              alt={selectedImage.title}
              className="max-w-full max-h-[85vh] object-contain rounded-lg"
            />
            <p className="text-center text-white mt-4">{selectedImage.title}</p>
          </motion.div>
        </motion.div>
      )}
    </div>
  );
};

export default BSDCLIProject;
